import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const SERVICES = [
  {
    icon: "ri-newspaper-line",
    title: "影视作品资讯",
    description: "按发布时间更新电影与电视剧的最新动态，涵盖海报、主创与上映信息。",
  },
  {
    icon: "ri-book-open-line",
    title: "剧情介绍与解析",
    description: "为每部作品提供结构清晰的剧情概述与分段详情，帮助你先了解再决定看什么。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选与搜索",
    description: "支持按年份、地区、评分等级筛选与排序，也能通过关键词快速定位作品。",
  },
  {
    icon: "ri-links-line",
    title: "相关资源展示",
    description: "基于题材与类型进行相关推荐，串联同类作品，方便顺藤摸瓜地发现好内容。",
  },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 新金瓶梅影视资源与剧情介绍平台",
    description:
      "了解新金瓶梅影视资源与剧情介绍平台：我们的定位、服务内容、联系方式以及版权与免责声明。",
    keywords: "新金瓶梅,关于我们,影视资讯平台,剧情介绍,联系我们",
  });

  return (
    <div className="page-shell pb-8 pt-24 md:pt-28">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <section className="mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
          <i className="ri-information-line" aria-hidden="true"></i>
          关于我们
        </span>
        <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
          专注影视资讯与剧情介绍的浏览平台
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          新金瓶梅影视资源与剧情介绍平台面向喜爱影视剧情解析、寻找影视资讯的观众与爱好者。
          我们把散落的作品信息整理成清晰的频道、分类与详情页，让你在几分钟内就能了解一部作品讲了什么、
          由谁出演、口碑如何，从而更从容地安排自己的观影清单。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/movies"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-movie-2-line" aria-hidden="true"></i>
            浏览电影频道
          </Link>
          <Link
            to="/search"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-search-line" aria-hidden="true"></i>
            搜索影视作品
          </Link>
        </div>
      </section>

      <section className="mt-10 md:mt-14">
        <h2 className="flex items-center gap-2.5">
          <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
          <span className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            我们提供什么
          </span>
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
          {SERVICES.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                <i className={item.icon} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 md:mt-14 lg:grid-cols-2 lg:gap-8">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="flex items-center gap-2.5">
            <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
            <span className="font-heading text-xl font-extrabold text-foreground-950">联系方式</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            如果你对站内内容有任何建议，或希望反馈错误信息，欢迎通过下面的入口与我们联系。
            我们会在收到后尽快查看并处理。
          </p>
          <ul className="mt-5 space-y-3">
            <li className="flex items-center gap-3 rounded-md bg-background-100 px-4 py-3">
              <i className="ri-mail-line text-lg text-primary-500" aria-hidden="true"></i>
              <a
                href="mailto:hello@xinjinpingmei.example"
                className="text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
              >
                hello@xinjinpingmei.example
              </a>
            </li>
            <li className="flex items-center gap-3 rounded-md bg-background-100 px-4 py-3">
              <i className="ri-feedback-line text-lg text-primary-500" aria-hidden="true"></i>
              <a
                href="mailto:hello@xinjinpingmei.example?subject=内容反馈"
                className="text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
              >
                内容反馈入口
              </a>
            </li>
          </ul>
          <p className="mt-4 text-xs text-foreground-400">
            为保证处理效率，请勿在邮件中附带任何敏感信息。
          </p>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="flex items-center gap-2.5">
            <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
            <span className="font-heading text-xl font-extrabold text-foreground-950">版权与免责声明</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
            <p>
              本站为影视资讯与剧情介绍的信息展示平台，站内所有影视作品名称、海报、演职人员、评分与剧情内容
              均为用于演示的示例数据，与任何现实中的作品、人物或机构无对应关系。
            </p>
            <p>
              本站不提供任何影视作品的在线播放、下载或资源存储服务，也不对第三方内容的真实性作出保证。
              如本站内容无意中侵犯了你的合法权益，请通过上方邮箱与我们联系，我们会及时核实并处理。
            </p>
            <p>本站内容仅供学习交流使用，未经许可不得用于任何商业用途。</p>
          </div>
        </div>
      </section>
    </div>
  );
}